import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { JOB_OPENINGS } from '../shared/careers';
import { careersAPI } from '../../services/api';
import { socketService } from '../../services/socketService';

export interface Job {
    id: string;
    title: string;
    department: string;
    location: string;
    workMode?: string;
    type: string;
    experience?: string;
    salary?: string;
    description: string;
    jobSummary?: string;
    requirements?: string[];
    responsibilities?: string[];
    preferredQualifications?: string[];
    benefits?: string[];
    teamInfo?: string;
    status?: 'active' | 'inactive' | 'draft';
    visibility?: 'visible' | 'hidden';
    applicants?: number;
    postedDate?: string;
    expirationDate?: string;
    applicationLink?: string;
    relocationSupport?: boolean;
}

interface JobsContextType {
    jobs: Job[];
    addJob: (job: Omit<Job, 'id'>) => void;
    updateJob: (id: string, job: Partial<Job>) => void;
    deleteJob: (id: string) => void;
    getJobById: (id: string) => Job | undefined;
    refreshJobs: () => Promise<void>;
    isLoading: boolean;
}

const JobsContext = createContext<JobsContextType | undefined>(undefined);

const STORAGE_KEY = 'amasqis_jobs';

interface JobsProviderProps {
    children: ReactNode;
    isAdmin?: boolean;  // When true, fetches from admin endpoint with visibility info
}

export function JobsProvider({ children, isAdmin = false }: JobsProviderProps) {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch jobs from API on mount
    const refreshJobs = async () => {
        setIsLoading(true);
        try {
            // Use admin endpoint when in admin mode, public otherwise
            const response = isAdmin 
                ? await careersAPI.getAllAdmin({ page: 1, limit: 1000 })
                : await careersAPI.getAll({ page: 1, limit: 1000 });
            const fetchedJobs = response.data || response.careers || response.items || [];
            
            // Map API response to Job format
            const mappedJobs = fetchedJobs.map((job: any) => ({
                id: job._id || job.id,
                title: job.title,
                department: job.department,
                location: job.location,
                workMode: job.workMode,
                type: job.type,
                experience: job.experience,
                salary: job.salary,
                description: job.description,
                jobSummary: job.jobSummary,
                requirements: job.requirements || [],
                responsibilities: job.responsibilities || [],
                preferredQualifications: job.preferredQualifications || [],
                benefits: job.benefits || [],
                teamInfo: job.teamInfo,
                status: job.status || 'active',
                visibility: job.visibility || 'visible',
                applicants: job.applicants || 0,
                postedDate: job.postedDate || job.createdAt,
                expirationDate: job.expirationDate,
                applicationLink: job.applicationLink,
                relocationSupport: job.relocationSupport
            }));

            setJobs(mappedJobs);
            
            // Also save to localStorage as backup
            localStorage.setItem(STORAGE_KEY, JSON.stringify(mappedJobs));
        } catch (error) {
            console.error('Error fetching jobs from API:', error);
            setJobs([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refreshJobs();
    }, []);

    // Real-time socket updates for careers
    useEffect(() => {
        // Connect to socket for real-time updates
        socketService.connect();

        // Subscribe to career events
        const unsubscribeCreated = socketService.subscribe('career:created', (event) => {
            if (!event.data?.career) return;
            const newJob = event.data.career;
            const mappedJob: Job = {
                id: newJob._id || newJob.id,
                title: newJob.title,
                department: newJob.department,
                location: newJob.location,
                workMode: newJob.workMode,
                type: newJob.type,
                experience: newJob.experience,
                salary: newJob.salary,
                description: newJob.description,
                jobSummary: newJob.jobSummary,
                requirements: newJob.requirements || [],
                responsibilities: newJob.responsibilities || [],
                preferredQualifications: newJob.preferredQualifications || [],
                benefits: newJob.benefits || [],
                teamInfo: newJob.teamInfo,
                status: newJob.status || 'active',
                visibility: newJob.visibility || 'visible',
                applicants: newJob.applicants || 0,
                postedDate: newJob.postedDate || newJob.createdAt,
                expirationDate: newJob.expirationDate,
                applicationLink: newJob.applicationLink,
                relocationSupport: newJob.relocationSupport
            };
            // Only add if status is active and visible (for public page)
            if (!isAdmin && (mappedJob.status !== 'active' || mappedJob.visibility !== 'visible')) {
                return;
            }
            setJobs(prev => [mappedJob, ...prev.filter(j => j.id !== mappedJob.id)]);
        });

        const unsubscribeUpdated = socketService.subscribe('career:updated', (event) => {
            if (!event.data?.career) return;
            const updatedJob = event.data.career;
            const jobId = updatedJob._id || updatedJob.id;
            
            setJobs(prev => {
                // For public page, remove job if it's no longer active/visible
                if (!isAdmin && (updatedJob.status !== 'active' || updatedJob.visibility !== 'visible')) {
                    return prev.filter(j => j.id !== jobId);
                }
                
                // Update existing job or add if not exists
                const exists = prev.some(j => j.id === jobId);
                if (exists) {
                    return prev.map(job => {
                        if (job.id === jobId) {
                            return {
                                ...job,
                                ...updatedJob,
                                id: jobId,
                                requirements: updatedJob.requirements || job.requirements,
                                responsibilities: updatedJob.responsibilities || job.responsibilities,
                                preferredQualifications: updatedJob.preferredQualifications || job.preferredQualifications,
                                benefits: updatedJob.benefits || job.benefits,
                            };
                        }
                        return job;
                    });
                }
                return prev;
            });
        });

        const unsubscribeDeleted = socketService.subscribe('career:deleted', (event) => {
            if (!event.data) return;
            const deletedId = event.data.career?._id || event.data.career?.id || event.data.id;
            if (!deletedId) return;
            setJobs(prev => prev.filter(job => job.id !== deletedId));
        });

        return () => {
            unsubscribeCreated();
            unsubscribeUpdated();
            unsubscribeDeleted();
        };
    }, [isAdmin]);

    const addJob = async (jobData: Omit<Job, 'id'>) => {
        try {
            // Map Job type to CareerData type for API - send all fields
            const careerData: any = {
                title: jobData.title,
                department: jobData.department,
                location: jobData.location,
                type: jobData.type,
                experience: jobData.experience || '',
                description: jobData.description,
                requirements: jobData.requirements,
                responsibilities: jobData.responsibilities,
                benefits: jobData.benefits,
                status: jobData.status,
                workMode: jobData.workMode,
                salary: jobData.salary,
                jobSummary: jobData.jobSummary,
                preferredQualifications: jobData.preferredQualifications,
                teamInfo: jobData.teamInfo,
                relocationSupport: jobData.relocationSupport,
                expirationDate: jobData.expirationDate,
                postedDate: jobData.postedDate || new Date().toISOString(),
            };
            
            // Add to API
            const response = await careersAPI.create(careerData);
            const newJob = response.data || response.career || response;
            
            // Map response to Job format
            const mappedJob: Job = {
                id: newJob._id || newJob.id,
                title: newJob.title,
                department: newJob.department,
                location: newJob.location,
                workMode: newJob.workMode,
                type: newJob.type,
                experience: newJob.experience,
                salary: newJob.salary,
                description: newJob.description,
                jobSummary: newJob.jobSummary,
                requirements: newJob.requirements || [],
                responsibilities: newJob.responsibilities || [],
                preferredQualifications: newJob.preferredQualifications || [],
                benefits: newJob.benefits || [],
                teamInfo: newJob.teamInfo,
                status: newJob.status || 'draft',
                visibility: newJob.visibility || 'visible',
                applicants: newJob.applicants || 0,
                postedDate: newJob.postedDate || newJob.createdAt,
                expirationDate: newJob.expirationDate,
                applicationLink: newJob.applicationLink,
                relocationSupport: newJob.relocationSupport
            };
            
            setJobs(prevJobs => [mappedJob, ...prevJobs]);
        } catch (error) {
            console.error('Error adding job to API:', error);
            
            // Fallback to local only
            const newJob: Job = {
                ...jobData,
                id: `job-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                postedDate: jobData.postedDate || new Date().toISOString(),
                applicants: 0,
                status: jobData.status || 'draft'
            };
            setJobs(prevJobs => [newJob, ...prevJobs]);
        }
    };

    const updateJob = async (id: string, jobData: Partial<Job>) => {
        try {
            // Update in API
            await careersAPI.update(id, jobData);
            
            // Update local state
            setJobs(prevJobs => 
                prevJobs.map(job => 
                    job.id === id ? { ...job, ...jobData } : job
                )
            );
        } catch (error) {
            console.error('Error updating job in API:', error);
            
            // Still update locally as fallback
            setJobs(prevJobs => 
                prevJobs.map(job => 
                    job.id === id ? { ...job, ...jobData } : job
                )
            );
        }
    };

    const deleteJob = async (id: string) => {
        try {
            // Delete from API (soft delete)
            await careersAPI.delete(id);
            
            // Remove from local state
            setJobs(prevJobs => prevJobs.filter(job => job.id !== id));
        } catch (error) {
            console.error('Error deleting job from API:', error);
            
            // Still delete locally as fallback
            setJobs(prevJobs => prevJobs.filter(job => job.id !== id));
        }
    };

    const getJobById = (id: string): Job | undefined => {
        return jobs.find(job => job.id === id);
    };

    return (
        <JobsContext.Provider value={{ jobs, addJob, updateJob, deleteJob, getJobById, refreshJobs, isLoading }}>
            {children}
        </JobsContext.Provider>
    );
}

export function useJobs() {
    const context = useContext(JobsContext);
    if (context === undefined) {
        throw new Error('useJobs must be used within a JobsProvider');
    }
    return context;
}